import * as React from "react";
import { Box, Typography } from "@mui/material";
import { CalendarCell } from "./calendar-utils";
import { Holiday } from "../../../../../../services/npi-holiday-service";

interface NpiCalendarDayCellProps {
    cell: CalendarCell;
    compact?: boolean;
}

const pickPrimaryHoliday = (holidays: Holiday[]): Holiday => {
    const nationalBrasil = holidays.find((holiday) => holiday.country === "BRASIL" && holiday.scope === "national");
    if (nationalBrasil) {
        return nationalBrasil;
    }
    const chinaFeriado = holidays.find((holiday) => holiday.country === "CHINA" && holiday.holiday_type !== "tiaoxiu");
    if (chinaFeriado) {
        return chinaFeriado;
    }
    return holidays[0];
};

const getCellStyle = (holidays: Holiday[], isWeekend: boolean) => {
    if (holidays.length === 0) {
        return isWeekend
            ? { borderColor: "#DCE0E5", backgroundColor: "#EEF0F2", color: "#9AA1A9" }
            : { borderColor: "#CFE0F5", backgroundColor: "#FFFFFF", color: "#1F2937" };
    }

    const holiday = pickPrimaryHoliday(holidays);

    if (holiday.country === "CHINA") {
        if (holiday.holiday_type === "tiaoxiu") {
            return { borderColor: "#F0A93C", backgroundColor: "#FEF6E8", color: "#B4650B" };
        }
        return { borderColor: "#E85D5D", backgroundColor: "#FDECEC", color: "#C02929" };
    }

    if (holiday.scope === "MANAUS") {
        return { borderColor: "#3FBF9F", backgroundColor: "#E9FBF6", color: "#1F7A63" };
    }
    if (holiday.scope === "SAO_PAULO") {
        return { borderColor: "#4D8AF0", backgroundColor: "#EAF2FE", color: "#2A5FC7" };
    }
    return { borderColor: "#E85D5D", backgroundColor: "#FDECEC", color: "#C02929" };
};

const NpiCalendarDayCellComponent: React.FC<NpiCalendarDayCellProps> = ({ cell, compact }) => {
    if (cell.day === null) {
        return <Box sx={{ minHeight: compact ? 34 : 78 }} />;
    }

    const style = getCellStyle(cell.holidays, cell.isWeekend);
    const primaryHoliday = cell.holidays.length > 0 ? pickPrimaryHoliday(cell.holidays) : null;
    const extraCount = cell.holidays.length - 1;

    return (
        <Box
            sx={{
                border: `1px solid ${style.borderColor}`,
                backgroundColor: style.backgroundColor,
                borderRadius: "6px",
                minHeight: compact ? 34 : 78,
                p: compact ? 0.5 : 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: 0.25,
            }}>
            <Typography sx={{ fontSize: compact ? 11 : 14, fontWeight: 700, color: style.color, lineHeight: 1 }}>
                {cell.day}
            </Typography>
            <Box>
                {!compact && primaryHoliday && (
                    <Typography
                        key={primaryHoliday.id}
                        sx={{ fontSize: 10, fontWeight: 600, color: style.color, lineHeight: 1.1, textAlign: "center" }}
                        noWrap
                    >
                        {primaryHoliday.description}{extraCount > 0 ? ` +${extraCount}` : ""}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default NpiCalendarDayCellComponent;